import { useState, useEffect, useCallback } from 'react';
import {
  fetchBoardGames, fetchAllOdds, fetchOracle,
  fetchLiveGames, fetchTrackRecord, fetchPlayResults, fetchGamePitchers,
  fetchBatters, fetchTodaysPitchers, fetchAiPredictions,
  fetchBookStates, fetchGameCounts, fetchLineMoves,
} from './supabase';

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

export function useBoard(date) {
  const targetDate = date || today();
  const [games,       setGames]       = useState([]);
  const [odds,        setOdds]        = useState({});
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      const rawGames = await fetchBoardGames(targetDate);
      // ── MERGE-BY-ID: prevents scroll jump on every 30s poll ──
      // Replacing the array causes React to remount the list and
      // reset scroll position. Merging by game_id updates data
      // in place so the DOM stays stable between polls.
      if (showLoader) {
        setGames(rawGames);
      } else {
        setGames(prev => {
          if (!prev.length) return rawGames;
          const map = new Map(prev.map(g => [String(g.game_id), g]));
          rawGames.forEach(g => map.set(String(g.game_id), { ...map.get(String(g.game_id)), ...g }));
          const merged = [];
          const seen   = new Set();
          prev.forEach(g => {
            const updated = map.get(String(g.game_id));
            if (updated) { merged.push(updated); seen.add(String(g.game_id)); }
          });
          rawGames.forEach(g => { if (!seen.has(String(g.game_id))) merged.push(g); });
          return merged;
        });
      }
      const ids = rawGames.map(g => g.game_id);
      if (ids.length) {
        const rawOdds = await fetchAllOdds(ids);
        setOdds(rawOdds);
      }
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('[useBoard]', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [targetDate]);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => {
    if (targetDate !== today()) return;
    const id = setInterval(() => load(false), 30_000);
    return () => clearInterval(id);
  }, [load, targetDate]);

  return { games, odds, loading, error, lastUpdated, refresh: () => load(true) };
}

export function useOracle() {
  const [oracle,  setOracle]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchOracle(today());
        if (!cancelled) { setOracle(data); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    const id = setInterval(load, 5 * 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return { oracle, loading, error };
}

export function useYesterday() {
  const [record,  setRecord]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrackRecord(30)
      .then(data => { setRecord(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return { record, loading };
}

// ── FIX: merge incoming data into existing state by game_id ───
// Previously called setGames(newArray) which replaced the entire
// array every 30s, causing React to remount every LiveGameCard
// and reset scroll position. Now we merge by game_id so existing
// components update in-place without unmounting.
export function useLiveGames() {
  const [games,   setGames]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const newData = await fetchLiveGames();
        setGames(prev => {
          // Build a map from existing games for O(1) lookup
          const map = new Map(prev.map(g => [String(g.game_id), g]));
          // Merge — update existing entries, add new ones
          newData.forEach(g => map.set(String(g.game_id), { ...map.get(String(g.game_id)), ...g }));
          // Preserve existing order, append any new games at end
          const merged = [];
          const seen = new Set();
          prev.forEach(g => {
            const updated = map.get(String(g.game_id));
            if (updated) { merged.push(updated); seen.add(String(g.game_id)); }
          });
          newData.forEach(g => {
            if (!seen.has(String(g.game_id))) merged.push(g);
          });
          return merged;
        });
      } catch (err) {
        console.error('[useLiveGames]', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  return { games, loading };
}

// ── FIX: same merge pattern for game pitchers ─────────────────
// Prevents PitcherTank from remounting every 30s during live games
export function useGamePitchers(gameId) {
  const [pitchers, setPitchers] = useState([]);
  useEffect(() => {
    if (!gameId) return;
    const load = async () => {
      try {
        const newData = await fetchGamePitchers(gameId);
        setPitchers(prev => {
          const map = new Map(prev.map(p => [String(p.pitcher_id), p]));
          newData.forEach(p => map.set(String(p.pitcher_id), { ...map.get(String(p.pitcher_id)), ...p }));
          const merged = [];
          const seen = new Set();
          prev.forEach(p => {
            const updated = map.get(String(p.pitcher_id));
            if (updated) { merged.push(updated); seen.add(String(p.pitcher_id)); }
          });
          newData.forEach(p => {
            if (!seen.has(String(p.pitcher_id))) merged.push(p);
          });
          return merged;
        });
      } catch (err) {
        console.error('[useGamePitchers]', err);
      }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [gameId]);
  return pitchers;
}

export function useLineMoves(gameId) {
  const [moves, setMoves] = useState([]);
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    const load = () =>
      fetchLineMoves(gameId)
        .then(data => { if (!cancelled) setMoves(data); })
        .catch(console.error);
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [gameId]);
  return moves;
}

export function useBatters() {
  const [batters, setBatters] = useState([]);
  useEffect(() => {
    fetchBatters().then(data => {
      setBatters(data.map(b => ({
        name:            b.name,
        team:            b.teams?.abbr || b.team_id,
        hr:              b.hr_season,
        games:           b.games_played,
        since:           b.games_since_last_hr,
        avg_between:     parseFloat(b.avg_games_between_hr),
        due:             b.due_factor,
        adjusted_due:    b.adjusted_due_factor || b.due_factor,
        pitcher_factor:  b.pitcher_factor,
        park_factor_adj: b.park_factor_adj,
        hand_factor:     b.handedness_factor,
        opp_pitcher:     b.opposing_pitcher,
        opp_hand:        b.opposing_pitcher_hand,
        venue:           b.todays_venue,
        is_active:       b.is_active,
        roster_status:   b.roster_status,
        batting_avg:     b.batting_avg,
        slugging_pct:    b.slugging_pct,
      })));
    }).catch(console.error);
  }, []);
  return batters;
}

export function useTodaysPitchers() {
  const [pitchers, setPitchers] = useState([]);
  useEffect(() => {
    fetchTodaysPitchers().then(setPitchers).catch(console.error);
  }, []);
  return pitchers;
}

export function useAiPredictions() {
  const [predictions, setPredictions] = useState([]);
  const [loading,     setLoading]     = useState(true);

  const load = useCallback(() => {
    fetchAiPredictions()
      .then(data => { setPredictions(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  return { predictions, loading, refresh: load };
}

export function useBookStates() {
  const [bookStates, setBookStates] = useState({});
  const [userState,  setUserState]  = useState(null);

  useEffect(() => {
    fetchBookStates().then(setBookStates).catch(console.error);
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
        const data = await res.json();
        const stateCode = data.address?.['ISO3166-2-lvl4']?.replace('US-', '') || null;
        if (stateCode) setUserState(stateCode);
      } catch {}
    }, () => {});
  }, []);

  const isAvailable = useCallback((book) => {
    if (!userState || !bookStates[book]) return true;
    return bookStates[book].has(userState);
  }, [userState, bookStates]);

  return { userState, isAvailable };
}

export function usePlays() {
  const [plays,   setPlays]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlayResults(50)
      .then(data => { setPlays(data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return { plays, loading };
}

export function useScheduleSummary() {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      dates.push(d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));
    }
    fetchGameCounts(dates).then(setCounts).catch(console.error);
  }, []);

  return counts;
}